import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Import the filter primitives DIRECTLY (not via the barrel) to keep
// the import graph explicit + tree-shake the heavier `<DateRangeFilter>`
// only where it's used (we do use it here — `dateFrom` / `dateTo`).
import { DateRangeFilter } from '../../../components/filters/DateRangeFilter';
import { FilterBar } from '../../../components/filters/FilterBar';
import { SearchInput } from '../../../components/filters/SearchInput';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import { MIN_SEARCH_LENGTH, STATUS_OPTIONS, type ReportFilters } from '../_lib/reportFilters';

/**
 * Filter toolbar for the reports moderation list (AUT-127) — search +
 * status multi-select + inspector UUID free-text + date range.
 * Extracted from the page so the page stays under the line cap. Every
 * value is URL-state-driven via the parent's `setFilters` patch.
 *
 * Inspector is a UUID free-text TextField (mockup shows a dropdown chip,
 * but with the backend's exact-match inspectorId filter the simplest
 * correct UI for v1 is a labelled UUID field — a typeahead selector is
 * a follow-up).
 */
export interface ReportsFilterBarProps {
  filters: ReportFilters;
  setFilters: (
    patch: Partial<Record<keyof ReportFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const ReportsFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: ReportsFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <SearchInput
        initialValue={filters.q ?? ''}
        placeholder={t('reports.search.placeholder')}
        testId="reports-search-input"
        minLength={MIN_SEARCH_LENGTH}
        onSearch={(q) => setFilters({ q: q || null })}
      />
      <StatusMultiSelect
        status={filters.status ?? []}
        options={STATUS_OPTIONS}
        label={t('reports.filters.statusLabel')}
        onChange={(next) => setFilters({ status: next })}
      />
      <TextField
        size="small"
        label={t('reports.filters.inspectorLabel')}
        placeholder={t('reports.filters.inspectorPlaceholder')}
        value={filters.inspectorId ?? ''}
        onChange={(e) => setFilters({ inspectorId: e.target.value || null })}
        sx={{ minWidth: 200 }}
      />
      <DateRangeFilter
        from={filters.dateFrom ?? null}
        to={filters.dateTo ?? null}
        onChange={(range) => setFilters({ dateFrom: range.from, dateTo: range.to })}
      />
    </FilterBar>
  );
};
